<script setup lang="ts">
import { computed, watch } from 'vue';
import { AppTypography } from '~/components';

const {
  errorMessages = [],
  readonly = false,
  disabled = false,
  hideDetails = false,
  label = '',
  placeholder = '',
  bgColor = 'white',
  rounded = 'lg',
  required = false,
  maxLength = 0,
  count = false,
} = defineProps<{
  errorMessages?: string | string[];
  readonly?: boolean;
  disabled?: boolean;
  hideDetails?: boolean | 'auto';
  label?: string;
  placeholder?: string;
  bgColor?: string;
  rounded?: string | number | boolean;
  required?: boolean;
  maxLength?: number;
  count?: boolean;
}>();

const emit = defineEmits<{
  (e: 'maxlength-exceeded', value: boolean): void;
}>();

const text = defineModel<string>();

// Check if maxlength is exceeded
const isMaxLengthExceeded = computed(() => {
  return maxLength > 0 && (text?.length ?? 0) > maxLength;
});

// Emit event when maxlength is exceeded
watch(isMaxLengthExceeded, (newValue) => {
  emit('maxlength-exceeded', newValue);
});

// Also watch text changes to ensure we catch everything
watch(text, (newText) => {
  if (maxLength > 0 && newText && newText.length > maxLength) {
    emit('maxlength-exceeded', true);
  } else if (maxLength > 0 && newText && newText.length <= maxLength) {
    emit('maxlength-exceeded', false);
  }
});
</script>
<template>
  <div class="position-relative">
    <v-textarea
      v-model="text"
      variant="solo"
      :hide-details="hideDetails"
      :error-messages="errorMessages"
      :placeholder="placeholder"
      :readonly="readonly"
      :disabled="disabled"
      :bg-color="bgColor"
      :rounded="rounded"
      data-testid="text-field"
      :class="{ 'error-state': isMaxLengthExceeded }"
      :maxlength="maxLength || false"
    >
      <template #label> {{ label }}<span v-if="required" class="text-error"> *</span> </template>
      <template #counter></template>
    </v-textarea>
    <AppTypography
      v-if="maxLength > 0"
      element="p"
      type="footnote-1"
      class="position-absolute bottom-0 right-0 mb-7 mr-4"
      :class="{'text-red-lighten-1': text?.length > maxLength}"

    >
      {{ text?.length || 0 }}/{{ maxLength }}
    </AppTypography>
  </div>
</template>

<style scoped>
:deep(.v-label) {
  color: #414156 !important;
  opacity: 1 !important;
}

:deep(.v-input__control) {
  overflow: visible;
}

:deep(.v-field) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: none !important;
  overflow: visible;
}

:deep(.v-field--focused) {
  border-color: #414156 !important;
  background-color: #fffce9 !important;
}

:deep(.v-field--error) {
  border-color: rgb(var(--v-theme-error)) !important;
}

:deep(.error-state .v-field) {
  border-color: rgb(var(--v-theme-error)) !important;
}

:deep(.v-field--error .v-field__outline),
:deep(.v-field__outline) {
  display: none;
}

:deep(.v-field__overlay) {
  display: none;
}

:deep(.v-text-field__prefix, .v-text-field__suffix) {
  font-weight: 700 !important;
  font-size: 24px !important;
  line-height: 24px !important;
}
</style>
